import { useState, useCallback, useRef } from 'react';
import { ZodError } from 'zod';
import {
  SubmitApplicationInputSchema,
  type Application,
  type SubmitApplicationInput
} from '@triedge/admissions-domain';
import { isAmplifyConfigured } from '../adapters/amplify-client';
import { submitApplication as submitAdmissionsApplication } from '../api/admissions';

export class ValidationError extends Error {
  constructor(
    message: string,
    public readonly zodError: ZodError,
    public readonly fieldErrors: Record<string, string[]>
  ) {
    super(message);
    this.name = 'ValidationError';
  }
}

export class SubmissionError extends Error {
  constructor(
    message: string,
    public readonly code: string,
    public readonly originalError?: unknown
  ) {
    super(message);
    this.name = 'SubmissionError';
  }
}

export interface SubmitApplicationState {
  submit: (data: unknown) => Promise<Application>;
  submitting: boolean;
  error: ValidationError | SubmissionError | null;
  lastResult: Application | null;
  clearError: () => void;
  reset: () => void;
}

export function useSubmitApplication(): SubmitApplicationState {
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<ValidationError | SubmissionError | null>(null);
  const [lastResult, setLastResult] = useState<Application | null>(null);
  const submittingRef = useRef(false);

  const submit = useCallback(async (data: unknown): Promise<Application> => {
    if (submittingRef.current) {
      throw new SubmissionError(
        'Application submission is already in progress',
        'SUBMISSION_IN_PROGRESS'
      );
    }

    if (!isAmplifyConfigured()) {
      throw new SubmissionError('Cannot submit: Bridge not configured', 'NOT_CONFIGURED');
    }

    submittingRef.current = true;
    setSubmitting(true);
    setError(null);

    try {
      const parseResult = SubmitApplicationInputSchema.safeParse(data);

      if (!parseResult.success) {
        const fieldErrors: Record<string, string[]> = {};

        for (const issue of parseResult.error.issues) {
          const path = issue.path.join('.');
          if (!fieldErrors[path]) {
            fieldErrors[path] = [];
          }
          fieldErrors[path].push(issue.message);
        }

        throw new ValidationError(
          `Validation failed: ${parseResult.error.issues.length} error(s)`,
          parseResult.error,
          fieldErrors
        );
      }

      const validatedInput: SubmitApplicationInput = parseResult.data;
      const result = await submitAdmissionsApplication(validatedInput);
      setLastResult(result);
      return result;
    } catch (exception) {
      if (exception instanceof ValidationError) {
        setError(exception);
        throw exception;
      }

      const submissionError = new SubmissionError(
        exception instanceof Error ? exception.message : 'Submission failed',
        'SUBMISSION_FAILED',
        exception
      );
      setError(submissionError);
      throw submissionError;
    } finally {
      submittingRef.current = false;
      setSubmitting(false);
    }
  }, []);

  const clearError = useCallback(() => {
    setError(null);
  }, []);

  const reset = useCallback(() => {
    setSubmitting(false);
    setError(null);
    setLastResult(null);
  }, []);

  return {
    submit,
    submitting,
    error,
    lastResult,
    clearError,
    reset
  };
}
